import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { getProjects, getTeamMembers, getAllocations, getSentWeeks, getSentWeekAllocations } from '../api/client'
import { currentWeekMonday, formatWeekRange } from '../utils/weeks'
import { useTranslation } from 'react-i18next'

// True if the given hex color is perceptually light (YIQ formula).
function isLightHex(hex) {
  if (!hex || typeof hex !== 'string') return false
  const h = hex.replace('#', '')
  if (h.length !== 6) return false
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return (r * 299 + g * 587 + b * 114) / 1000 > 155
}

export default function ThisWeek() {
  const { t } = useTranslation('engineering')
  const [week, setWeek] = useState(null)
  const [projects, setProjects] = useState([])
  const [members, setMembers] = useState([])
  const [allocations, setAllocations] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const [sentWeeks, projs, mems] = await Promise.all([
          getSentWeeks(),
          getProjects(),
          getTeamMembers(),
        ])
        if (cancelled) return

        // Use the most recently sent week; fall back to current calendar week
        const sorted = Array.isArray(sentWeeks)
          ? [...sentWeeks].sort((a, b) => b.week_start.localeCompare(a.week_start))
          : []
        const isSent = sorted.length > 0
        const resolvedWeek = isSent ? sorted[0].week_start : currentWeekMonday()
        setWeek(resolvedWeek)

        // For sent weeks, read from the frozen snapshot — live allocations may
        // have been cleared after sending. For non-sent weeks, use live data.
        const allocs = isSent
          ? await getSentWeekAllocations(resolvedWeek)
          : await getAllocations({ week_start: resolvedWeek })
        if (cancelled) return

        setProjects(Array.isArray(projs) ? projs.filter(p => p.is_active && !p.is_purchase_only) : [])
        setMembers(Array.isArray(mems) ? mems : [])
        setAllocations(Array.isArray(allocs) ? allocs : [])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  const memberById = useMemo(() => {
    const m = new Map()
    for (const mem of members) m.set(mem.id, mem)
    return m
  }, [members])

  const projectCards = useMemo(() => {
    const byProject = new Map()
    for (const a of allocations) {
      if (!a.hours || a.hours <= 0) continue
      const list = byProject.get(a.project_id) || []
      list.push(a)
      byProject.set(a.project_id, list)
    }

    return projects
      .map(p => {
        const allocs = byProject.get(p.id) || []
        const engineers = allocs
          .map(a => ({
            id: a.team_member_id,
            name: memberById.get(a.team_member_id)?.name || 'Unknown',
            hours: a.hours,
          }))
          .sort((a, b) => b.hours - a.hours || a.name.localeCompare(b.name))
        const totalHours = engineers.reduce((s, e) => s + e.hours, 0)
        return { project: p, engineers, totalHours }
      })
      .filter(c => c.engineers.length > 0)
      .sort((a, b) => b.totalHours - a.totalHours || a.project.name.localeCompare(b.project.name))
  }, [projects, allocations, memberById])

  return (
    <div className="p-8">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="min-w-0">
          <h1 className="text-[28px] font-bold text-gray-900 tracking-tight leading-tight">{t('thisWeek.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {week ? formatWeekRange(week) : '—'} · {t('thisWeek.subtitle')}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-gray-400 text-sm">{t('thisWeek.loading')}</div>
      ) : projectCards.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-sm">{t('thisWeek.noAllocations')}</p>
          <p className="text-xs mt-1">{t('thisWeek.noAllocationsHint')}</p>
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {projectCards.map(({ project, engineers, totalHours }) => {
            const headerBg = project.color || '#6366f1'
            const light = isLightHex(headerBg)
            const titleCls = light ? 'text-gray-900' : 'text-white'
            const subCls = light ? 'text-gray-700' : 'text-white/80'
            const mutedCls = light ? 'text-gray-600' : 'text-white/70'
            return (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className="block bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 hover:shadow-md transition-all"
              >
                <div
                  className="px-4 py-3 flex items-start justify-between gap-2"
                  style={{ backgroundColor: headerBg }}
                >
                  <div className="min-w-0">
                    <h3 className={`text-sm font-semibold truncate ${titleCls}`}>
                      {project.nickname || project.name}
                    </h3>
                    {project.code && (
                      <p className={`text-[11px] mt-0.5 truncate ${subCls}`}>{project.code}</p>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <p className={`text-sm font-semibold ${titleCls}`}>{totalHours}h</p>
                    <p className={`text-[10px] uppercase tracking-wider ${mutedCls}`}>{t('thisWeek.total')}</p>
                  </div>
                </div>

                <div className="p-4">
                  {project.po_pm && (
                    <p className="text-[11px] text-gray-500 mb-2 truncate">{t('thisWeek.pmpo', { name: project.po_pm })}</p>
                  )}

                  <ul className="space-y-1.5">
                    {engineers.map(e => (
                      <li key={e.id} className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-gray-700 truncate">{e.name}</span>
                        <span className="text-gray-500 tabular-nums shrink-0">{e.hours}h</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
